import React from 'react';

import PropTypes from 'prop-types';

import A from 'wanderu-component-react-router-link';
import { Typography } from 'wanderu-components';
import { localize } from 'wanderu-i18n';
import { durationToHoursAndMinutes } from 'wanderu-utils';

import styles from './CarrierStatsTable.scss';

const propTypes = {
    carrierData: PropTypes.arrayOf(PropTypes.shape({
        carrierCode: PropTypes.string,
        carrierName: PropTypes.string,
        carrierGuide: PropTypes.string,
        metrics: PropTypes.shape({
            averageDailyTrips: PropTypes.number,
            averageDurationMinutes: PropTypes.number,
            averagePriceUsd: PropTypes.number,
            routeCount: PropTypes.number,
            carrierRoutes: PropTypes.number,
            carrierCities: PropTypes.number,
        }),
    })),
    namespace: PropTypes.string,
};
const defaultProps = {
    carrierData: [],
    namespace: '',
};

function buildTableColumns(carrierData) {
    let hasRouteCount = false;
    let hasAvgMinutes = false;
    let hasCityCount = false;
    let hasAvgPrice = false;

    carrierData.forEach((data) => {
        const { metrics = {} } = data;
        const {
            averageDurationMinutes,
            averagePriceUsd,
            routeCount,
            carrierRoutes,
            carrierCities,
        } = metrics;

        if (!!routeCount || !!carrierRoutes) {
            hasRouteCount = true;
        }

        if (averageDurationMinutes) {
            hasAvgMinutes = true;
        }

        if (carrierCities) {
            hasCityCount = true;
        }

        if (averagePriceUsd) {
            hasAvgPrice = true;
        }
    });

    return {
        hasAvgMinutes,
        hasAvgPrice,
        hasRouteCount,
        hasCityCount,
    };
}

function CarrierStatRow(props) {
    const {
        t,
        i18nMoment,
        i18nMoney,
        carrier = {},
    } = props;
    const {
        carrierName: name,
        carrierGuide,
        metrics = {},
    } = carrier;
    const {
        averageDailyTrips,
        averageDurationMinutes,
        averagePriceUsd,
        routeCount,
        carrierRoutes,
        carrierCities,
    } = metrics;

    const averageTime = averageDurationMinutes
        && i18nMoment.duration(averageDurationMinutes, 'minutes');
    const duration = durationToHoursAndMinutes(averageTime);
    const durationString = t(
        `baseApp:trip-duration_${duration.days > 0
            ? 'days'
            : 'hours'}`,
        duration,
    );
    const averagePrice = averagePriceUsd && i18nMoney(averagePriceUsd).format();

    const showThirdColumn = averageDurationMinutes !== undefined || carrierCities !== undefined;

    return (
        <tr className={styles.tableRow}>
            <td>
                {carrierGuide
                    ? (
                        <A
                            data-id="carrierGuideLink"
                            href={`/carrier/${carrierGuide}/`}
                        >
                            {name}
                        </A>
                    )
                    : (
                        <Typography>
                            {name}
                        </Typography>
                    )}
            </td>
            <td>
                {Math.round(averageDailyTrips || routeCount || carrierRoutes)}
            </td>
            {showThirdColumn && (
                <td>
                    {averageDurationMinutes
                        ? durationString
                        : carrierCities}
                </td>
            )}
            {!!averagePrice && (
                <td>
                    {averagePrice}
                </td>
            )}
        </tr>
    );
}

function CarrierStatsTable(props) {
    const {
        t,
        i18nMoment,
        i18nMoney,
        namespace,
        carrierData,
        color = 'secondary',
    } = props;

    const {
        hasAvgMinutes,
        hasAvgPrice,
        hasRouteCount,
        hasCityCount,
    } = buildTableColumns(carrierData);

    const showThirdColumn = hasAvgMinutes || hasCityCount;
    const showFourthColumn = hasAvgPrice;

    return (
        <div className={styles.container}>
            <table className={styles.carrierTable}>
                <thead>
                    <tr className={styles.tableHeader}>
                        <th>{t(`${namespace}:carrierTableHeader`)}</th>
                        <th>
                            {hasRouteCount
                                ? t(`${namespace}:routeCount`)
                                : t(`${namespace}:tripsTableHeader`)}
                        </th>
                        {showThirdColumn && (
                            <th>
                                {hasCityCount
                                    ? t(`${namespace}:cities`)
                                    : t(`${namespace}:shortAvgTime`)}
                            </th>
                        )}
                        {showFourthColumn && (
                            <th>
                                {t(`${namespace}:shortAvgPrice`)}
                            </th>
                        )}
                    </tr>
                </thead>
                <tbody>
                    {carrierData.map((carrier) => (
                        <CarrierStatRow
                            carrier={carrier}
                            color={color}
                            i18nMoment={i18nMoment}
                            i18nMoney={i18nMoney}
                            key={carrier.carrierCode}
                            t={t}
                        />
                    ))}
                </tbody>
            </table>
        </div>
    );
}

CarrierStatsTable.propTypes = propTypes;
CarrierStatsTable.defaultProps = defaultProps;

export {
    CarrierStatRow,
    CarrierStatsTable,
};
export default localize(CarrierStatsTable);
